import React, { useState, useEffect, useCallback } from 'react';
import {
  FaUsers,
  FaEdit,
  FaTrash,
  FaPlus,
  FaSearch,
  FaTimes,
  FaEye,
  FaEyeSlash,
  FaCheckCircle,
  FaTimesCircle,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import AdminHeader from '../../components/admin/AdminHeader';
import DriverCardSupport from '../../components/admin/DriverCardSupport';
import { userService } from '../../services/userService';
import { User, UserRole } from '../../types';
import { Select } from '../../components/ui';
import './Users.css';

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [proprietarios, setProprietarios] = useState<User[]>([]);
  const [showPassword, setShowPassword] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    cpfCnpj: '',
    telefone: '',
    nivelAcesso: UserRole.USUARIO,
    proprietarioId: '',
    statusAtivo: true,
  });

  const filterUsers = useCallback(() => {
    let filtered = [...users];

    // Filtrar por termo de busca
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.fullName.toLowerCase().includes(term) ||
          user.email.toLowerCase().includes(term) ||
          user.cpfCnpj?.toLowerCase().includes(term)
      );
    }

    // Filtrar por role
    if (filterRole !== 'all') {
      filtered = filtered.filter((user) => user.nivelAcesso === filterRole);
    }

    setFilteredUsers(filtered);
  }, [users, searchTerm, filterRole]);

  useEffect(() => {
    loadUsers();
    loadProprietarios();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, filterRole, filterUsers]);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const response = await userService.getAllUsers();
      if (response.success && response.data) {
        setUsers(response.data);
      } else {
        toast.error(response.message || 'Erro ao carregar usuários');
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setIsLoading(false);
    }
  };

  const loadProprietarios = async () => {
    try {
      const response = await userService.getProprietarios();
      if (response.success && response.data) {
        setProprietarios(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar proprietários:', error);
    }
  };


  const handleOpenModal = (user?: User) => {
    if (user) {
      setIsEditMode(true);
      setSelectedUser(user);
      setFormData({
        email: user.email,
        password: '',
        fullName: user.fullName,
        cpfCnpj: user.cpfCnpj || '',
        telefone: user.telefone || '',
        nivelAcesso: user.nivelAcesso,
        proprietarioId: user.proprietarioId || '',
        statusAtivo: user.statusAtivo,
      });
    } else {
      setIsEditMode(false);
      setSelectedUser(null);
      setFormData({
        email: '',
        password: '',
        fullName: '',
        cpfCnpj: '',
        telefone: '',
        nivelAcesso: UserRole.USUARIO,
        proprietarioId: '',
        statusAtivo: true,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsEditMode(false);
    setSelectedUser(null);
    setShowPassword(false);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validações
    if (!formData.fullName || !formData.email) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    if (!isEditMode && !formData.password) {
      toast.error('A senha é obrigatória para novos usuários');
      return;
    }

    if (formData.nivelAcesso === UserRole.MOTORISTA && !formData.proprietarioId) {
      toast.error('Selecione um proprietário para o motorista');
      return;
    }

    try {
      let response;

      const dataToSend: any = {
        email: formData.email,
        fullName: formData.fullName,
        cpfCnpj: formData.cpfCnpj || undefined,
        telefone: formData.telefone || undefined,
        nivelAcesso: formData.nivelAcesso,
        statusAtivo: formData.statusAtivo,
      };

      // Incluir proprietarioId apenas para motoristas
      if (formData.nivelAcesso === UserRole.MOTORISTA) {
        dataToSend.proprietarioId = formData.proprietarioId;
      }

      // Incluir senha apenas se preenchida
      if (formData.password) {
        dataToSend.password = formData.password;
      }

      if (isEditMode && selectedUser) {
        response = await userService.updateUser(selectedUser.id, dataToSend);
      } else {
        response = await userService.createUser(dataToSend);
      }

      if (response.success) {
        toast.success(response.message);
        handleCloseModal();
        loadUsers();
      } else {
        toast.error(response.message);
      }
    } catch (error: any) {
      console.error('Erro ao salvar usuário:', error);
      toast.error('Erro ao salvar usuário');
    }
  };

  const handleDelete = async (user: User) => {
    if (!window.confirm(`Deseja realmente excluir o usuário ${user.fullName}?`)) {
      return;
    }

    try {
      const response = await userService.deleteUser(user.id);
      if (response.success) {
        toast.success(response.message);
        loadUsers();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      toast.error('Erro ao excluir usuário');
    }
  };

  const getRoleBadgeClass = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return 'role-badge admin';
      case UserRole.PARCEIRO:
        return 'role-badge parceiro';
      case UserRole.PROPRIETARIO:
        return 'role-badge proprietario';
      case UserRole.MOTORISTA:
        return 'role-badge motorista';
      default:
        return 'role-badge usuario';
    }
  };

  const getRoleLabel = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return 'Admin';
      case UserRole.PARCEIRO:
        return 'Parceiro';
      case UserRole.PROPRIETARIO:
        return 'Proprietário';
      case UserRole.MOTORISTA:
        return 'Motorista';
      default:
        return 'Usuário';
    }
  };

  return (
    <div className="users-page">
      <AdminHeader />
      <div className="users-container">
        <div className="users-header">
          <div className="users-title">
            <FaUsers className="title-icon" />
            <h1>Gerenciamento de Usuários</h1>
          </div>
          <button className="btn-primary" onClick={() => handleOpenModal()}>
            <FaPlus /> Novo Usuário
          </button>
        </div>

        <div className="users-filters">
          <div className="search-box">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Buscar por nome, email ou CPF/CNPJ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filter-select-wrapper">
            <Select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              options={[
                { value: 'all', label: 'Todos os tipos' },
                { value: UserRole.ADMIN, label: 'Admin' },
                { value: UserRole.PARCEIRO, label: 'Parceiro' },
                { value: UserRole.PROPRIETARIO, label: 'Proprietário' },
                { value: UserRole.MOTORISTA, label: 'Motorista' },
                { value: UserRole.USUARIO, label: 'Usuário' },
              ]}
              placeholder="Filtrar por tipo"
              fullWidth={false}
              size="md"
              className="filter-select"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Carregando usuários...</p>
          </div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>CPF/CNPJ</th>
                  <th>Telefone</th>
                  <th>Tipo</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="no-data">
                      Nenhum usuário encontrado
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td className="user-name">{user.fullName}</td>
                      <td>{user.email}</td>
                      <td>{user.cpfCnpj || '-'}</td>
                      <td>{user.telefone || '-'}</td>
                      <td>
                        <span className={getRoleBadgeClass(user.nivelAcesso)}>
                          {getRoleLabel(user.nivelAcesso)}
                        </span>
                      </td>
                      <td>
                        {user.statusAtivo ? (
                          <span className="status-badge active">
                            <FaCheckCircle /> Ativo
                          </span>
                        ) : (
                          <span className="status-badge inactive">
                            <FaTimesCircle /> Inativo
                          </span>
                        )}
                      </td>
                      <td className="actions-cell">
                        <button
                          className="btn-icon edit"
                          onClick={() => handleOpenModal(user)}
                          title="Editar"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="btn-icon delete"
                          onClick={() => handleDelete(user)}
                          title="Excluir"
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Cadastro/Edição */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {isEditMode ? 'Editar Usuário' : 'Novo Usuário'}
              </h2>
              <button className="modal-close" onClick={handleCloseModal}>
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="user-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="fullName">
                    Nome Completo <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">
                    Email <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="password">
                    Senha {!isEditMode && <span className="required">*</span>}
                  </label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required={!isEditMode}
                      placeholder={isEditMode ? 'Deixe vazio para não alterar' : ''}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="cpfCnpj">CPF/CNPJ</label>
                  <input
                    type="text"
                    id="cpfCnpj"
                    name="cpfCnpj"
                    value={formData.cpfCnpj}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="telefone">Telefone</label>
                  <input
                    type="text"
                    id="telefone"
                    name="telefone"
                    value={formData.telefone}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <Select
                    label="Tipo de Usuário"
                    name="nivelAcesso"
                    value={formData.nivelAcesso}
                    onChange={handleInputChange}
                    options={[
                      { value: UserRole.USUARIO, label: 'Usuário' },
                      { value: UserRole.MOTORISTA, label: 'Motorista' },
                      { value: UserRole.PROPRIETARIO, label: 'Proprietário' },
                      { value: UserRole.PARCEIRO, label: 'Parceiro' },
                      { value: UserRole.ADMIN, label: 'Admin' },
                    ]}
                    required
                  />
                </div>

                {formData.nivelAcesso === UserRole.MOTORISTA && (
                  <div className="form-group">
                    <Select
                      label="Proprietário"
                      name="proprietarioId"
                      value={formData.proprietarioId}
                      onChange={handleInputChange}
                      options={[
                        { value: '', label: 'Selecione um proprietário' },
                        ...proprietarios.map((proprietario) => ({
                          value: proprietario.id,
                          label: proprietario.fullName,
                        }))
                      ]}
                      required
                    />
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="statusAtivo"
                      checked={formData.statusAtivo}
                      onChange={handleInputChange}
                    />
                    <span>Usuário ativo</span>
                  </label>
                </div>
              </div>

              {isEditMode &&
                selectedUser &&
                (selectedUser.nivelAcesso === UserRole.USUARIO ||
                  selectedUser.nivelAcesso === UserRole.MOTORISTA) && (
                  <DriverCardSupport user={selectedUser} onChanged={loadUsers} />
                )}

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleCloseModal}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {isEditMode ? 'Salvar Alterações' : 'Criar Usuário'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;

